import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Key, CheckCircle2, XCircle, Pencil, Eye, EyeOff, ExternalLink, Zap, Loader2, Sparkles, CreditCard, Mail, Layers, LogIn } from "lucide-react";

const SERVICE_COLORS = {
  IA: "bg-sky-100 text-sky-700",
  Pagamentos: "bg-amber-100 text-amber-700",
  Email: "bg-emerald-100 text-emerald-700",
  Agregadores: "bg-violet-100 text-violet-700",
  "Login social": "bg-slate-100 text-slate-700",
};

const SERVICE_ICONS = {
  IA: Sparkles,
  Pagamentos: CreditCard,
  Email: Mail,
  Agregadores: Layers,
  "Login social": LogIn,
};

const SERVICE_ORDER = ["IA", "Agregadores", "Google for Jobs", "Pagamentos", "Email", "Login social"];

const SERVICE_HELP = {
  IA: {
    intro: "OpenAI (chave própria) para moderação de anúncios, geração de vagas, análise de CV e match score. Sem chave, as funções de IA ficam desligadas e os anúncios das empresas ficam pendentes para revisão manual.",
    where: "Obtém a chave em platform.openai.com/api-keys (Create new secret key). Modelo por defeito: gpt-4o-mini (AI_MODEL no .env para mudar).",
    link: "https://platform.openai.com/api-keys",
  },
  Pagamentos: {
    intro: "Chaves Stripe para checkout de destaques e packs de créditos. Enquanto vazias, o botão “Comprar” devolve 503.",
    where: "Dashboard Stripe → Developers → API keys (Secret + Publishable). Webhook: /api/stripe/webhook.",
    link: "https://dashboard.stripe.com/apikeys",
  },
  Email: {
    intro: "Provedor Resend para envio de emails transacionais (candidaturas, alertas, boost activado). Sem chave, os emails vão para log.",
    where: "resend.com/api-keys. Adiciona um domínio verificado antes de ir para produção.",
    link: "https://resend.com/api-keys",
  },
  Agregadores: {
    intro: "Conectores multi-fonte que importam vagas de portais externos. As APIs oficiais (Adzuna, Jooble, Careerjet) pedem uma chave por serviço. Os sites de scraping (SAPO, Expresso, Manpower, Randstad) partilham a mesma ScraperAPI Key.",
    where: "ScraperAPI: dashboard.scraperapi.com. Adzuna: developer.adzuna.com (regista-te e cria uma app). Jooble: jooble.org/api/about. Careerjet: careerjet.com/partners/api.",
    link: "https://dashboard.scraperapi.com/",
  },
  "Login social": {
    intro: "OAuth 2.0 do Google para permitir “Entrar com Google” aos candidatos e empresas.",
    where: "Google Cloud Console → APIs & Services → Credentials → OAuth 2.0 Client IDs. Redirect URI: {PUBLIC_BASE_URL}/api/auth/google/callback.",
    link: "https://console.cloud.google.com/apis/credentials",
  },
};

function KeyRow({ keyName, meta, onEdit, onTest, testing, testResult }) {
  return (
    <div data-testid={`config-key-${keyName}`}
      className="bg-white rounded-xl border border-black/5 p-5 flex flex-wrap items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <p className="font-heading font-bold tracking-tighter text-slate-900">{meta.label}</p>
          <Badge className={`${SERVICE_COLORS[meta.service] || "bg-slate-100 text-slate-600"} hover:${SERVICE_COLORS[meta.service] || ""} text-[10px]`}>
            {meta.service}
          </Badge>
          {meta.configured ? (
            <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" /> Configurada
              {meta.source === "db" && <span className="text-slate-400">(BD)</span>}
              {meta.source === "env" && <span className="text-slate-400">(env)</span>}
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-red-500 font-medium">
              <XCircle className="w-3.5 h-3.5" /> Não configurada
            </span>
          )}
          {testResult && (
            <span data-testid={`config-test-result-${keyName}`} className={`flex items-center gap-1 text-xs font-medium ${testResult.ok ? "text-emerald-600" : "text-red-500"}`}>
              {testResult.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              {testResult.ok ? `OK · ${testResult.latency_ms}ms` : "Falhou"}
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500">{meta.description}</p>
        {meta.redirect_uri && (
          <div className="mt-2 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 shrink-0">Redirect URI</span>
            <code className="text-[11px] font-mono text-slate-700 truncate flex-1" title={meta.redirect_uri}>{meta.redirect_uri}</code>
            <button
              type="button"
              data-testid={`copy-redirect-${keyName}`}
              onClick={() => { navigator.clipboard.writeText(meta.redirect_uri); }}
              className="text-[11px] font-semibold text-amber-600 hover:text-amber-700 shrink-0"
            >
              Copiar
            </button>
          </div>
        )}
        {testResult && !testResult.ok && (
          <p className="text-xs text-red-500 mt-1 font-mono truncate" title={testResult.message}>{testResult.message}</p>
        )}
        {testResult && testResult.ok && testResult.message && (
          <p className="text-xs text-emerald-600 mt-1 truncate" title={testResult.message}>{testResult.message}</p>
        )}
        {meta.configured && meta.masked_value && (
          <p className="text-xs font-mono text-slate-400 mt-1">{meta.masked_value}</p>
        )}
        {meta.updated_at && (
          <p className="text-[11px] text-slate-400 mt-0.5">
            Atualizada a {new Date(meta.updated_at).toLocaleString("pt-PT")}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        <Button size="sm" variant="outline" className="rounded-full"
          data-testid={`config-edit-btn-${keyName}`}
          onClick={() => onEdit(keyName, meta)}>
          <Pencil className="w-3.5 h-3.5 mr-1.5" /> {meta.configured ? "Atualizar" : "Configurar"}
        </Button>
        {meta.configured && (
          <Button
            size="sm"
            variant="outline"
            data-testid={`config-test-btn-${keyName}`}
            className="rounded-full"
            disabled={testing}
            onClick={() => onTest(keyName)}
          >
            {testing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
            Testar
          </Button>
        )}
      </div>
    </div>
  );
}

export function AdminApiKeysTab() {
  const [config, setConfig] = useState({});
  const [editKey, setEditKey] = useState(null); // { keyName, meta }
  const [value, setValue] = useState("");
  const [showValue, setShowValue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  const [activeService, setActiveService] = useState("IA");

  const load = useCallback(() => {
    api.get("/admin/config").then((r) => setConfig(r.data.config)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (keyName, meta) => {
    setEditKey({ keyName, meta });
    setValue("");
    setShowValue(false);
  };

  const testKey = async (keyName) => {
    setTesting((t) => ({ ...t, [keyName]: true }));
    try {
      const { data } = await api.post("/admin/config/test", { key: keyName });
      setTestResults((r) => ({ ...r, [keyName]: data }));
      if (data.ok) toast.success(`${keyName}: OK (${data.latency_ms}ms)`);
      else toast.error(`${keyName}: ${data.message}`);
    } catch (err) {
      toast.error(formatErr(err));
      setTestResults((r) => ({ ...r, [keyName]: { ok: false, message: formatErr(err), latency_ms: 0 } }));
    } finally {
      setTesting((t) => ({ ...t, [keyName]: false }));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!value.trim()) { toast.error("Introduz um valor"); return; }
    setSaving(true);
    try {
      await api.patch("/admin/config", { key: editKey.keyName, value: value.trim() });
      toast.success(`${editKey.meta.label} guardada!`);
      setEditKey(null);
      load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  // Agrupar por service preservando SERVICE_ORDER e adicionando services desconhecidos ao fim
  const grouped = Object.entries(config).reduce((acc, [k, v]) => {
    const s = v.service || "Outros";
    if (!acc[s]) acc[s] = [];
    acc[s].push([k, v]);
    return acc;
  }, {});
  const services = [
    ...SERVICE_ORDER.filter((s) => grouped[s]),
    ...Object.keys(grouped).filter((s) => !SERVICE_ORDER.includes(s)),
  ];

  const totalConfigured = Object.values(config).filter(v => v.configured).length;
  const total = Object.keys(config).length;

  // se o service atualmente ativo desaparecer (por acaso), cai no primeiro
  useEffect(() => {
    if (services.length > 0 && !services.includes(activeService)) {
      setActiveService(services[0]);
    }
  }, [services, activeService]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-black/5 p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-heading font-bold tracking-tighter text-slate-900 flex items-center gap-2">
            <Key className="w-5 h-5 text-amber-500" /> Estado das integrações
          </p>
          <p className="text-sm text-slate-500 mt-0.5">
            {totalConfigured} de {total} chaves configuradas
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {services.map((s) => {
            const count = grouped[s]?.filter(([, v]) => v.configured).length || 0;
            const totalS = grouped[s]?.length || 0;
            return (
              <span key={s}
                className={`text-xs rounded-full px-3 py-1.5 border ${count === totalS ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                {s}: <b>{count}/{totalS}</b>
              </span>
            );
          })}
        </div>
      </div>

      {/* Tabs por serviço */}
      <Tabs value={activeService} onValueChange={setActiveService}>
        <TabsList data-testid="api-keys-tabs" className="flex flex-wrap h-auto justify-start gap-1 bg-slate-100/60 p-1 rounded-xl">
          {services.map((s) => {
            const Icon = SERVICE_ICONS[s] || Key;
            const count = grouped[s]?.filter(([, v]) => v.configured).length || 0;
            const totalS = grouped[s]?.length || 0;
            return (
              <TabsTrigger
                key={s}
                value={s}
                data-testid={`api-keys-tab-${s.toLowerCase().replace(/\s/g, "-")}`}
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-3 py-2 gap-2 text-sm"
              >
                <Icon className="w-4 h-4" />
                <span>{s}</span>
                <Badge className={`ml-1 text-[10px] ${count === totalS && totalS > 0 ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600 hover:bg-slate-200"}`}>
                  {count}/{totalS}
                </Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {services.map((s) => {
          const help = SERVICE_HELP[s];
          return (
            <TabsContent key={s} value={s} className="mt-5 space-y-4">
              {help && (
                <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-4">
                  <p className="text-sm text-slate-700"><b>{s}</b> — {help.intro}</p>
                  <p className="text-xs text-slate-500 mt-1">Onde obter: {help.where}</p>
                  {help.link && (
                    <a href={help.link} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 mt-2 font-medium">
                      Abrir portal <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
              <div className="space-y-3">
                {grouped[s]?.map(([k, v]) => (
                  <KeyRow key={k} keyName={k} meta={v} onEdit={openEdit} onTest={testKey} testing={testing[k]} testResult={testResults[k]} />
                ))}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={!!editKey} onOpenChange={(o) => { if (!o) setEditKey(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-black tracking-tighter">
              {editKey?.meta.configured ? "Atualizar" : "Configurar"} {editKey?.meta.label}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <p className="text-sm text-slate-600">{editKey?.meta.description}</p>
            <div className="relative">
              <Input
                type={showValue ? "text" : "password"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={editKey?.meta.configured ? "Nova chave (deixa em branco para manter)" : "Introduz a chave..."}
                className="pr-10 font-mono text-sm"
                autoComplete="off"
                data-testid="config-edit-input"
              />
              <button type="button" tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowValue(!showValue)}>
                {showValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {editKey?.meta.configured && (
              <p className="text-xs text-slate-400">
                Chave atual: <span className="font-mono">{editKey.meta.masked_value}</span>
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setEditKey(null)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="config-edit-save-btn" className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={saving}>
                {saving ? "A guardar..." : "Guardar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
